import { useEffect, useMemo, useState } from "react";

const DISMISS_KEY = "schoola-mobile-web-app-prompt-dismissed";
const PUBLIC_WEBSITE_PATH_PREFIXES = ["/p/", "/blog"];
const IOS_APP_URL =
  process.env.REACT_APP_IOS_APP_URL || process.env.REACT_APP_MOBILE_APP_URL || "";
const ANDROID_APP_URL =
  process.env.REACT_APP_ANDROID_APP_URL || process.env.REACT_APP_MOBILE_APP_URL || "";

function getPlatform(userAgent) {
  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "ios";
  }

  if (/Android/i.test(userAgent)) {
    return "android";
  }

  return "other";
}

export function isPublicWebsitePath(pathname) {
  const currentPathname = pathname || "/";

  if (currentPathname === "/" || currentPathname === "/blog") {
    return true;
  }

  return PUBLIC_WEBSITE_PATH_PREFIXES.some((prefix) =>
    currentPathname.startsWith(prefix)
  );
}

export function useMobileWebAppPrompt(pathname) {
  const currentPathname = pathname || "/";
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    return localStorage.getItem(DISMISS_KEY) === "1";
  });
  const [deviceState, setDeviceState] = useState({
    isMobileBrowser: false,
    isStandalone: false,
    platform: "other",
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateDeviceState = () => {
      const userAgent = navigator.userAgent || navigator.vendor || "";
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      const compactViewport = window.matchMedia?.("(max-width: 900px)").matches ?? false;
      const hasTouch = navigator.maxTouchPoints > 1;
      const isMobileBrowser =
        /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(userAgent) ||
        (compactViewport && hasTouch);

      setDeviceState({
        isMobileBrowser,
        isStandalone,
        platform: getPlatform(userAgent),
      });
    };

    updateDeviceState();
    window.addEventListener("resize", updateDeviceState);

    return () => {
      window.removeEventListener("resize", updateDeviceState);
    };
  }, []);

  const storeUrl = useMemo(() => {
    if (deviceState.platform === "ios") {
      return IOS_APP_URL;
    }

    if (deviceState.platform === "android") {
      return ANDROID_APP_URL;
    }

    return IOS_APP_URL || ANDROID_APP_URL;
  }, [deviceState.platform]);

  const shouldPrompt =
    !isDismissed &&
    !!storeUrl &&
    deviceState.isMobileBrowser &&
    deviceState.platform !== "ios" &&
    !deviceState.isStandalone &&
    !isPublicWebsitePath(currentPathname);

  const dismissPrompt = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setIsDismissed(true);
  };

  return {
    dismissPrompt,
    platform: deviceState.platform,
    shouldPrompt,
    storeUrl,
  };
}
